import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { ResidentProfile } from "../../../models/residentProfile";
import { STAFF_ACCESS_MATRIX_ROWS, StaffAccessMatrixAction } from "./StaffAccessMatrix";
import { createEmptyStaffDelegationRequirements, StaffDelegationRequirements } from "../../../constants/staffDelegationRequirements";

interface AddCaregiverModalProps {
  homeId?: string;
  residents?: ResidentProfile[];
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  onAdded?: () => Promise<void> | void;
}

type StaffResult = {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  userRoleType?: string;
  selectedRoleTypes?: string[];
  matchedRoleTypes?: string[];
  roleTypes?: string[];
};

const STAFF_ROLE_OPTIONS = [
  { value: ROLE_TYPE_PROVIDER, label: "Facility Admin" },
  { value: ROLE_TYPE_CAREGIVER, label: "Caregiver" },
  { value: ROLE_TYPE_NURSE, label: "Registered Nurse" },
  { value: ROLE_TYPE_MHP, label: "Mental Health Professional" },
  { value: ROLE_TYPE_FACILITY_WORKER, label: "Facility Worker" },
];

const SHIFT_DAY_OPTIONS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const ALL_TIME_SHIFT_START = "00:00";
const ALL_TIME_SHIFT_END = "23:59";

const HALF_HOUR_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? "00" : "30";
  const value = `${String(hour).padStart(2, "0")}:${minutes}`;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const meridiem = hour < 12 ? "AM" : "PM";
  return {
    value,
    label: `${hour12}:${minutes} ${meridiem}`,
  };
});

const createDailyShiftSchedule = () => SHIFT_DAY_OPTIONS.map((option) => ({
  day: option.key,
  label: option.label,
  enabled: false,
  startTime: "08:00",
  endTime: "16:00",
}));

const buildShiftWindowsFromSchedule = (
  schedule: Array<{ day: string; enabled: boolean; startTime: string; endTime: string }>,
) => (
  schedule
    .filter((item) => item.enabled)
    .map((item) => ({
      day: item.day,
      startTime: item.startTime,
      endTime: item.endTime,
    }))
);

const buildAllTimeShiftWindows = () => (
  SHIFT_DAY_OPTIONS.map((option) => ({
    day: option.key,
    startTime: ALL_TIME_SHIFT_START,
    endTime: ALL_TIME_SHIFT_END,
  }))
);

const applyShiftPresetForDays = (
  schedule: Array<{ day: string; label: string; enabled: boolean; startTime: string; endTime: string }>,
  dayKeys: string[],
  startTime: string,
  endTime: string,
) => (
  schedule.map((item) => ({
    ...item,
    enabled: dayKeys.includes(item.day),
    startTime,
    endTime,
  }))
);

const clearShiftSchedule = (
  schedule: Array<{ day: string; label: string; enabled: boolean; startTime: string; endTime: string }>,
) => (
  schedule.map((item) => ({
    ...item,
    enabled: false,
  }))
);

const formatRoleLabel = (roleType: string) => {
  if (roleType === ROLE_TYPE_PROVIDER) {
    return "Facility Admin";
  }
  if (roleType === ROLE_TYPE_CAREGIVER) {
    return "Caregiver";
  }
  if (roleType === ROLE_TYPE_NURSE) {
    return "Registered Nurse";
  }
  if (roleType === ROLE_TYPE_MHP) {
    return "Mental Health Professional";
  }
  return String(roleType || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStaffRoleTypes = (user?: StaffResult | null) => Array.from(new Set(
  [
    String(user?.userRoleType || "").trim().toLowerCase(),
    ...(Array.isArray(user?.selectedRoleTypes) ? (user?.selectedRoleTypes || []) : []),
    ...(Array.isArray(user?.matchedRoleTypes) ? (user?.matchedRoleTypes || []) : []),
    ...(Array.isArray(user?.roleTypes) ? (user?.roleTypes || []) : []),
  ].filter(Boolean),
));

const LINKABLE_WIZARD_ROLE_TYPES = [ROLE_TYPE_PROVIDER, ROLE_TYPE_CAREGIVER, ROLE_TYPE_NURSE, ROLE_TYPE_MHP, ROLE_TYPE_FACILITY_WORKER];
const WIZARD_ACCESS_PRESET_CAREGIVER = "caregiver";
const WIZARD_ACCESS_PRESET_NURSE = "nurse";
const WIZARD_ACCESS_PRESET_MHP = "mhp";
const WIZARD_ACCESS_PRESET_PROVIDER = "provider";
const WIZARD_ACCESS_PRESET_FACILITY_WORKER = "facility_worker";

const isArchivedLinkedStaff = (linkedStaff: any) => {
  const archivedAt = String(linkedStaff?.archivedAt || "").trim();
  if (archivedAt) {
    return true;
  }
  const activeValue = linkedStaff?.active;
  if (typeof activeValue === "string") {
    return activeValue.trim().toLowerCase() === "false";
  }
  return activeValue === false || activeValue === 0;
};

const getAssignableWizardRoleTypes = (users: StaffResult[]) => {
  const normalizedUsers = (Array.isArray(users) ? users : []).filter(Boolean);
  if (!normalizedUsers.length) {
    return [];
  }

  return LINKABLE_WIZARD_ROLE_TYPES.filter((roleType) => normalizedUsers.every((user) => getStaffRoleTypes(user).includes(roleType)));
};

const getWizardAccessPresetOptions = (users: StaffResult[]) => {
  const assignableRoleTypes = getAssignableWizardRoleTypes(users);
  return assignableRoleTypes.map((roleType) => {
    if (roleType === ROLE_TYPE_CAREGIVER) {
      return {
        value: WIZARD_ACCESS_PRESET_CAREGIVER,
        roleType,
        label: "Caregiver",
        description: "Daily actions, Search Care Plan, and eMAR.",
      };
    }
    if (roleType === ROLE_TYPE_NURSE) {
      return {
        value: WIZARD_ACCESS_PRESET_NURSE,
        roleType,
        label: "Registered Nurse",
        description: "Clinical access plus daily actions and eMAR.",
      };
    }
    if (roleType === ROLE_TYPE_PROVIDER) {
      return {
        value: WIZARD_ACCESS_PRESET_PROVIDER,
        roleType,
        label: "Facility Admin",
        description: "Facility administration and management access.",
      };
    }
    if (roleType === ROLE_TYPE_FACILITY_WORKER) {
      return {
        value: WIZARD_ACCESS_PRESET_FACILITY_WORKER,
        roleType,
        label: "Facility Worker",
        description: "Assigned facility tasks only; no resident or clinical access.",
      };
    }
    return {
      value: WIZARD_ACCESS_PRESET_MHP,
      roleType,
      label: "Mental Health Professional",
      description: "Clinical access plus daily actions and eMAR, without delegation.",
    };
  });
};

const getClinicalAccessConfigForPreset = (preset: string) => {
  if (preset === WIZARD_ACCESS_PRESET_NURSE) {
    return {
      roleType: ROLE_TYPE_NURSE,
      assessmentAccessEnabled: true,
      carePlanAccessEnabled: true,
      delegationAccessEnabled: true,
      binderAccessEnabled: true,
    };
  }
  if (preset === WIZARD_ACCESS_PRESET_MHP) {
    return {
      roleType: ROLE_TYPE_MHP,
      assessmentAccessEnabled: true,
      carePlanAccessEnabled: true,
      delegationAccessEnabled: false,
      binderAccessEnabled: true,
    };
  }
  return null;
};

const getRoleTypeForWizardPreset = (preset: string) => {
  if (preset === WIZARD_ACCESS_PRESET_CAREGIVER) return ROLE_TYPE_CAREGIVER;
  if (preset === WIZARD_ACCESS_PRESET_NURSE) return ROLE_TYPE_NURSE;
  if (preset === WIZARD_ACCESS_PRESET_MHP) return ROLE_TYPE_MHP;
  if (preset === WIZARD_ACCESS_PRESET_PROVIDER) return ROLE_TYPE_PROVIDER;
  if (preset === WIZARD_ACCESS_PRESET_FACILITY_WORKER) return ROLE_TYPE_FACILITY_WORKER;
  return "";
};

const AddCaregiverModal: FC<AddCaregiverModalProps> = ({
  homeId,
  residents = [],
  isOpen,
  toggleModal,
  onAdded,
}) => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { backendAPIStore } = useContext(MobXProviderContext);
  const mobile = useResponsive().isMobile;
  const [loading, setLoading] = useState(false);
  const [loadingLinkedStaff, setLoadingLinkedStaff] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [searchEmailsInput, setSearchEmailsInput] = useState("");
  const [matchedUsers, setMatchedUsers] = useState<StaffResult[]>([]);
  const [unmatchedEmails, setUnmatchedEmails] = useState<string[]>([]);
  const [showStaffUnlockDialog, setShowStaffUnlockDialog] = useState(false);
  const [showInvitationSentDialog, setShowInvitationSentDialog] = useState(false);
  const [invitationSentEmail, setInvitationSentEmail] = useState("");
  const [selectedStaffUser, setSelectedStaffUser] = useState<StaffResult | null>(null);
  const [selectedStaffUsers, setSelectedStaffUsers] = useState<StaffResult[]>([]);
  const [selectedMatchedUserIds, setSelectedMatchedUserIds] = useState<string[]>([]);
  const [linkedStaffByUserId, setLinkedStaffByUserId] = useState<Record<string, any>>({});
  const [selectedWizardAccessPreset, setSelectedWizardAccessPreset] = useState("");
  const [showAccessMatrixDialog, setShowAccessMatrixDialog] = useState(false);
  const [wizardAccessStepError, setWizardAccessStepError] = useState("");
  const [selectedResidentIds, setSelectedResidentIds] = useState<string[]>([]);
  const [dailyShiftSchedule, setDailyShiftSchedule] = useState(createDailyShiftSchedule);
  const [bulkEveryDayStartTime, setBulkEveryDayStartTime] = useState("08:00");
  const [bulkEveryDayEndTime, setBulkEveryDayEndTime] = useState("16:00");
  const [allTimeShiftAccessEnabled, setAllTimeShiftAccessEnabled] = useState(false);
  const [mobileAccessEnabled, setMobileAccessEnabled] = useState(true);
  const [hourlyRateInput, setHourlyRateInput] = useState<number | null>(0);
  const [staffRequirements, setStaffRequirements] = useState<StaffDelegationRequirements>(createEmptyStaffDelegationRequirements);
  const [staffNameInput, setStaffNameInput] = useState("");
  const [staffPhoneInput, setStaffPhoneInput] = useState("");
  const activeResidents = (Array.isArray(residents) ? residents : []).filter((resident) => {
    const activeValue: any = resident?.active;
    if (typeof activeValue === "string") {
      return activeValue.trim().toLowerCase() !== "false";
    }
    return activeValue !== false && activeValue !== 0;
  });
  const activeResidentsById = useMemo(
    () => activeResidents.reduce((acc: Record<string, ResidentProfile>, resident) => {
      const residentId = String(resident?.residentId || "").trim();
      if (residentId) {
        acc[residentId] = resident;
      }
      return acc;
    }, {}),
    [activeResidents],
  );

  const isStaffLimitReachedError = (error: any) => (
    String(error?.message || error?.error || "")
      .trim()
      .toLowerCase()
      .includes("maximum number of staffs you can add")
  );

  const resetState = () => {
    setLoading(false);
    setCurrentStep(1);
    setSearchEmailsInput("");
    setMatchedUsers([]);
    setUnmatchedEmails([]);
    setShowStaffUnlockDialog(false);
    setSelectedStaffUser(null);
    setSelectedStaffUsers([]);
    setSelectedMatchedUserIds([]);
    setSelectedWizardAccessPreset("");
    setShowAccessMatrixDialog(false);
    setWizardAccessStepError("");
    setSelectedResidentIds([]);
    setDailyShiftSchedule(createDailyShiftSchedule());
    setBulkEveryDayStartTime("08:00");
    setBulkEveryDayEndTime("16:00");
    setAllTimeShiftAccessEnabled(false);
    setMobileAccessEnabled(true);
    setHourlyRateInput(0);
    setStaffRequirements(createEmptyStaffDelegationRequirements());
    setStaffNameInput("");
    setStaffPhoneInput("");
  };

  const linkedStaffIds = useMemo(() => new Set(Object.keys(linkedStaffByUserId)), [linkedStaffByUserId]);

  const selectedMatchedUsers = useMemo(
    () => matchedUsers.filter((user) => selectedMatchedUserIds.includes(String(user?.userId || "").trim())),
    [matchedUsers, selectedMatchedUserIds],
  );

  const applyWizardAccessPreset = (preset: string) => {
    setSelectedWizardAccessPreset(preset);
    if (preset === WIZARD_ACCESS_PRESET_FACILITY_WORKER) {
      setSelectedResidentIds([]);
      setCurrentStep(4);
      return;
    }
    if (preset) {
      setSelectedResidentIds(activeResidents
        .map((resident) => String(resident?.residentId || "").trim())
        .filter(Boolean));
    }
  };

  useEffect(() => {
    if (!isOpen || !homeId) {
      setLinkedStaffByUserId({});
      return;
    }

    let cancelled = false;
    const loadLinkedStaff = async () => {
      try {
        setLoadingLinkedStaff(true);
        await backendAPIStore.init();
        const linkedStaff = await backendAPIStore.getLinkedCaregiversForHome(homeId);
        if (cancelled) {
          return;
        }
        const nextByUserId = (Array.isArray(linkedStaff) ? linkedStaff : []).reduce((acc: Record<string, any>, item: any) => {
          const staffUserId = String(item?.caregiverId || item?.userId || "").trim();
          if (staffUserId) {
            acc[staffUserId] = item;
          }
          return acc;
        }, {});
        setLinkedStaffByUserId(nextByUserId);
      } catch {
        if (!cancelled) {
          setLinkedStaffByUserId({});
        }
      } finally {
        if (!cancelled) {
          setLoadingLinkedStaff(false);
        }
      }
    };

    void loadLinkedStaff();
    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, homeId, isOpen]);

  const onSearchSubmit = async () => {
    const normalizedEmail = String(searchEmailsInput || "").trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }
    const normalizedEmails = [normalizedEmail];

    setLoading(true);
    setMatchedUsers([]);
    setSelectedStaffUser(null);
    setSelectedStaffUsers([]);
    setSelectedMatchedUserIds([]);
    setUnmatchedEmails([]);

    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.searchActors({
        emails: normalizedEmails,
      });
      const rows = Array.isArray(response) ? response : [];
      const normalizedRows = Array.from(
        rows.reduce((acc: Map<string, any>, item: any) => {
          const userId = String(item?.userId || "").trim();
          const email = String(item?.email || "").trim().toLowerCase();
          const dedupeKey = userId || email;
          if (!dedupeKey) {
            return acc;
          }

          const roleTypes = Array.from(new Set(
            [
              String(item?.userRoleType || "").trim().toLowerCase(),
              ...(Array.isArray(item?.selectedRoleTypes) ? item.selectedRoleTypes : []),
              ...(Array.isArray(item?.matchedRoleTypes) ? item.matchedRoleTypes : []),
            ]
              .map((roleType: any) => String(roleType || "").trim().toLowerCase())
              .filter(Boolean),
          ));

          const existingItem = acc.get(dedupeKey);
          if (!existingItem) {
            acc.set(dedupeKey, {
              ...item,
              roleTypes,
            });
            return acc;
          }

          acc.set(dedupeKey, {
            ...existingItem,
            ...item,
            roleTypes: Array.from(new Set([
              ...(Array.isArray(existingItem?.roleTypes) ? existingItem.roleTypes : []),
              ...roleTypes,
            ])),
          });
          return acc;
        }, new Map<string, any>()).values(),
      );
      const matchedEmailSet = new Set(normalizedRows.map((item: any) => String(item?.email || "").trim().toLowerCase()).filter(Boolean));
      setMatchedUsers(normalizedRows);
      setUnmatchedEmails(normalizedEmails.filter((email) => !matchedEmailSet.has(email)));
    } catch (error: any) {
      setUnmatchedEmails(normalizedEmails);
    } finally {
      setLoading(false);
    }
  };

  const onToggleMatchedUserSelection = (userId: string, checked: boolean) => {
    setSelectedMatchedUserIds((current) => {
      if (checked) {
        return current.includes(userId) ? current : [...current, userId];
      }
      return current.filter((value) => value !== userId);
    });
  };

  const onContinueWithSelected = () => {
    if (!selectedMatchedUsers.length) {
      toast.current?.show({ severity: "warn", summary: "Select Staff", detail: "Choose one or more matched staff first.", life: 2500 });
      return;
    }
    const assignableRoleTypes = getAssignableWizardRoleTypes(selectedMatchedUsers);
    if (!assignableRoleTypes.length) {
      toast.current?.show({
        severity: "warn",
        summary: "No Shared Role",
        detail: "The selected staff need at least one shared Caregiver, Nurse, or MHP role to continue together.",
        life: 3000,
      });
      return;
    }
    setSelectedStaffUsers(selectedMatchedUsers);
    setSelectedStaffUser(selectedMatchedUsers[0] || null);
    setSelectedWizardAccessPreset(assignableRoleTypes.includes(ROLE_TYPE_FACILITY_WORKER) ? WIZARD_ACCESS_PRESET_FACILITY_WORKER : "");
    setWizardAccessStepError("");
    setSelectedResidentIds([]);
    setCurrentStep(selectedMatchedUsers.some((user) => !String(user?.userId || "").trim())
      ? 2
      : assignableRoleTypes.includes(ROLE_TYPE_FACILITY_WORKER) ? 4 : 3);
  };

  const createStaffLink = async (user: StaffResult, includeResidentShareSettings: boolean, includeShiftSettings = includeResidentShareSettings) => {
    if (!homeId || !user?.userId) {
      return;
    }

    const selectedRoleType = selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_NURSE
      ? ROLE_TYPE_NURSE
      : selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_MHP
        ? ROLE_TYPE_MHP
        : selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_PROVIDER
          ? ROLE_TYPE_PROVIDER
          : selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_FACILITY_WORKER
            ? ROLE_TYPE_FACILITY_WORKER
            : ROLE_TYPE_CAREGIVER;
    const roleTypes = selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_FACILITY_WORKER
      ? [ROLE_TYPE_FACILITY_WORKER]
      : Array.from(new Set(
      [
        String(user?.userRoleType || "").trim().toLowerCase(),
        ...(Array.isArray(user?.selectedRoleTypes) ? user.selectedRoleTypes : []),
        ...(Array.isArray(user?.matchedRoleTypes) ? user.matchedRoleTypes : []),
      ].filter(Boolean),
    ));
    const primaryRoleType = String(
      includeResidentShareSettings && roleTypes.includes(selectedRoleType)
        ? selectedRoleType
        : user?.userRoleType || roleTypes[0] || "",
    ).trim().toLowerCase();
    const shiftWindows = includeShiftSettings
      ? (allTimeShiftAccessEnabled ? buildAllTimeShiftWindows() : buildShiftWindowsFromSchedule(dailyShiftSchedule))
      : [];

    await backendAPIStore.addHomeCaregiverLink(homeId, user.userId, {
      primaryRoleType,
      roleTypes,
      mobileAccessEnabled,
      hourlyRate: Number(hourlyRateInput || 0),
      shiftEnabled: shiftWindows.length > 0,
      shiftWindows,
      shiftTimezone: "America/Los_Angeles",
    });

    if (!includeResidentShareSettings) {
      return;
    }

    await Promise.all(
      selectedResidentIds.map((residentId) =>
        backendAPIStore.addCaregiverResidentShare({
          homeId,
          caregiverId: user.userId,
          residentId,
        }),
      ),
    );
  };

  const createStaffLinks = async (users: StaffResult[], includeResidentShareSettings: boolean) => {
    const normalizedUsers = users.filter((user) => String(user?.userId || "").trim());
    for (const user of normalizedUsers) {
      await createStaffLink(user, false, true);
    }

    if (!includeResidentShareSettings || !homeId) {
      return;
    }

    const normalizedUserIds = normalizedUsers
      .map((user) => String(user?.userId || "").trim())
      .filter(Boolean);
    const accessUserIds = normalizedUsers
      .map((user) => String(user?.userId || "").trim())
      .filter(Boolean);

    if (!selectedResidentIds.length || !accessUserIds.length) {
      return;
    }

    const clinicalAccessConfig = getClinicalAccessConfigForPreset(selectedWizardAccessPreset);

    if (clinicalAccessConfig) {
      await Promise.all(
        accessUserIds.flatMap((caregiverId) =>
          selectedResidentIds.map((residentId) => {
            const resident = activeResidentsById[residentId];
            return backendAPIStore.createResidentRoleLink({
              providerResidentId: residentId,
              linkedActorId: caregiverId,
              roleType: clinicalAccessConfig.roleType,
              assessmentAccessEnabled: clinicalAccessConfig.assessmentAccessEnabled,
              carePlanAccessEnabled: clinicalAccessConfig.carePlanAccessEnabled,
              delegationAccessEnabled: clinicalAccessConfig.delegationAccessEnabled,
              binderAccessEnabled: clinicalAccessConfig.binderAccessEnabled,
              metadata: {
                providerHomeId: String((resident as any)?.homeId || homeId || "").trim(),
              },
            });
          }),
        ),
      );
    }

    await Promise.all(
      accessUserIds.flatMap((caregiverId) =>
        selectedResidentIds.map((residentId) =>
          backendAPIStore.addCaregiverResidentShare({
            homeId,
            caregiverId,
            residentId,
          }),
        ),
      ),
    );
  };

  const onAddStaffClick = async (user: StaffResult) => {
    setStaffNameInput(`${user.firstName || ""} ${user.lastName || ""}`.trim());
    setStaffPhoneInput("");
    const assignableRoleTypes = getAssignableWizardRoleTypes([user]);
    if (assignableRoleTypes.length) {
      setSelectedStaffUser(user);
      setSelectedStaffUsers([user]);
      const isFacilityWorker = assignableRoleTypes.includes(ROLE_TYPE_FACILITY_WORKER);
      setSelectedWizardAccessPreset(isFacilityWorker ? WIZARD_ACCESS_PRESET_FACILITY_WORKER : "");
      setWizardAccessStepError("");
      setSelectedResidentIds([]);
      setCurrentStep(String(user?.userId || "").trim() ? (isFacilityWorker ? 4 : 3) : 2);
      return;
    }

    setSelectedStaffUser(user);
    setSelectedStaffUsers([user]);
    setStaffRequirements(createEmptyStaffDelegationRequirements());
    setCurrentStep(2);
  };

  const onConfigureInvitedStaff = (email: string) => {
    const pendingStaff: StaffResult = {
      userId: "",
      email,
      roleTypes: [ROLE_TYPE_PROVIDER, ROLE_TYPE_CAREGIVER, ROLE_TYPE_NURSE, ROLE_TYPE_MHP, ROLE_TYPE_FACILITY_WORKER],
      selectedRoleTypes: [ROLE_TYPE_PROVIDER, ROLE_TYPE_CAREGIVER, ROLE_TYPE_NURSE, ROLE_TYPE_MHP, ROLE_TYPE_FACILITY_WORKER],
    };
    setSelectedStaffUser(pendingStaff);
    setSelectedStaffUsers([pendingStaff]);
    setSelectedWizardAccessPreset("");
    setWizardAccessStepError("");
    setSelectedResidentIds([]);
    setStaffNameInput("");
    setStaffPhoneInput("");
    setCurrentStep(2);
  };

  const onSaveStaffWizard = async () => {
    const targetUsers = selectedStaffUsers.length ? selectedStaffUsers : (selectedStaffUser ? [selectedStaffUser] : []);
    if (!targetUsers.length) return;
    const isFacilityWorker = selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_FACILITY_WORKER;
    if (!selectedWizardAccessPreset || (!isFacilityWorker && !selectedResidentIds.length)) {
      setWizardAccessStepError(!selectedWizardAccessPreset && (!isFacilityWorker && !selectedResidentIds.length)
        ? "Select at least one role and one resident before continuing."
          : !selectedWizardAccessPreset
            ? "Select at least one role before continuing."
          : "Select at least one resident before continuing.");
      setCurrentStep(3);
      return;
    }
    const nameParts = staffNameInput.trim().split(/\s+/).filter(Boolean);
    if (selectedStaffRequiresInvite && (nameParts.length < 2 || !staffPhoneInput.trim())) {
      toast.current?.show({ severity: "warn", summary: "Complete Staff Details", detail: "First name, last name, and phone number are required.", life: 3000 });
      setCurrentStep(2);
      return;
    }
    try {
      setLoading(true);
      await backendAPIStore.init();
      const resolvedTargetUsers: StaffResult[] = [];
      let createdAccountCount = 0;
      for (const user of targetUsers) {
        const account: any = await backendAPIStore.saveStaffAccount({
          roleType: getRoleTypeForWizardPreset(selectedWizardAccessPreset) || getStaffRoleTypes(user)[0] || ROLE_TYPE_CAREGIVER,
          staffId: user.userId || undefined,
          name: staffNameInput.trim(),
          email: String(user.email || "").trim().toLowerCase(),
          staffDetails: { ...staffRequirements, phoneNumber: staffPhoneInput.trim() },
        });
        if (String(account?.accountStatus || "").toLowerCase() === "created") {
          createdAccountCount += 1;
        }
        const resolvedUserId = String(account?.userId || user.userId || "").trim();
        if (!resolvedUserId) {
          throw new Error("The staff account was created but no user ID was returned, so access could not be configured.");
        }
        resolvedTargetUsers.push({ ...user, userId: resolvedUserId });
      }
      await createStaffLinks(resolvedTargetUsers, true);
      const invitationWasSent = createdAccountCount > 0;
      if (invitationWasSent) {
        setInvitationSentEmail(resolvedTargetUsers.map((user) => String(user.email || "").trim()).filter(Boolean).join(", "));
        setShowInvitationSentDialog(true);
      } else {
        toast.current?.show({ severity: "success", summary: "Staff Added", detail: `${resolvedTargetUsers.length} staff member${resolvedTargetUsers.length === 1 ? "" : "s"} added successfully with access configured.`, life: 3000 });
        resetState();
        toggleModal(false);
        window.location.reload();
      }
      await onAdded?.();
    } catch (error: any) {
      if (isStaffLimitReachedError(error)) {
        setShowStaffUnlockDialog(true);
        return;
      }
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to add staff.", life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const onProceedToShiftStep = () => {
    const isFacilityWorker = selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_FACILITY_WORKER;
    if (!selectedWizardAccessPreset || (!isFacilityWorker && !selectedResidentIds.length)) {
      setWizardAccessStepError(!selectedWizardAccessPreset && (!isFacilityWorker && !selectedResidentIds.length)
        ? "Select at least one role and one resident before continuing."
          : !selectedWizardAccessPreset
            ? "Select at least one role before continuing."
          : "Select at least one resident before continuing.");
      return;
    }
    setWizardAccessStepError("");
    setCurrentStep(4);
  };

  const onProceedToResidentsStep = () => {
    const nameParts = staffNameInput.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2 || !staffPhoneInput.trim()) {
      setWizardAccessStepError("Enter the staff member's full name and phone number before continuing.");
      return;
    }
    setWizardAccessStepError("");
    setCurrentStep(3);
  };

  const hasAnyEnabledShiftDay = dailyShiftSchedule.some((item) => item.enabled);
  const wizardAccessPresetOptions = useMemo(
    () => getWizardAccessPresetOptions(selectedStaffUsers.length ? selectedStaffUsers : (selectedStaffUser ? [selectedStaffUser] : [])),
    [selectedStaffUser, selectedStaffUsers],
  );
  const selectedWizardRoleLabel = useMemo(
    () => wizardAccessPresetOptions.find((option) => option.value === selectedWizardAccessPreset)?.label || "",
    [selectedWizardAccessPreset, wizardAccessPresetOptions],
  );

  const selectedStaffRequiresInvite = (selectedStaffUsers.length ? selectedStaffUsers : (selectedStaffUser ? [selectedStaffUser] : []))
    .some((user) => !String(user?.userId || "").trim());
  const isFacilityWorkerSelected = selectedWizardAccessPreset === WIZARD_ACCESS_PRESET_FACILITY_WORKER;

  const stepItems = [
    { step: 1, label: "Search Staff" },
    ...(selectedStaffRequiresInvite ? [{ step: 2, label: "Profile" }] : []),
    ...(!isFacilityWorkerSelected ? [{ step: 3, label: "Select Residents" }] : []),
    { step: 4, label: "Shift Details" },
  ];

  const headerElement = renderSurfaceHeader(
    "Manage Staff",
    "Search staff by email, review their roles, and link them to this facility.",
    "pi pi-users",
  );

  const wizardFooter = currentStep >= 2 ? (
    <div className="flex justify-content-between gap-2 flex-wrap w-full">
      {currentStep === 2 ? <>
        <Button type="button" label="Back" severity="secondary" rounded outlined onClick={() => setCurrentStep(1)} />
        <Button type="button" label="Next: Residents" severity="warning" rounded onClick={onProceedToResidentsStep} />
      </> : null}
      {currentStep === 3 ? <>
        <Button type="button" label="Back" severity="secondary" rounded outlined onClick={() => setCurrentStep(selectedStaffRequiresInvite ? 2 : 1)} />
        <Button type="button" label="Next: Shift" severity="warning" rounded onClick={onProceedToShiftStep} />
      </> : null}
      {currentStep === 4 ? <>
        <Button type="button" label="Back" severity="secondary" rounded outlined onClick={() => setCurrentStep(3)} />
        <Button type="button" label={selectedStaffRequiresInvite ? "Add Staff and Send Invite" : "Add Staff"} icon="pi pi-check" severity="warning" rounded loading={loading} onClick={() => void onSaveStaffWizard()} />
      </> : null}
    </div>
  ) : null;

  return (
    <Dialog
      visible={isOpen}
      modal
      header={headerElement}
      style={{ ...appDialogStyle, width: "min(92vw, 58rem)", minHeight: "min(78vh, 48rem)" }}
      headerStyle={appDialogHeaderStyle}
      contentStyle={{ ...appDialogContentStyle, minHeight: "min(62vh, 38rem)", maxHeight: "calc(100vh - 9rem)", overflowY: "auto" }}
      footer={wizardFooter}
      onHide={() => {
        if (!isOpen) return;
        resetState();
        toggleModal(false);
      }}
    >
      <Toast ref={toast} />
      <Dialog
        visible={showStaffUnlockDialog}
        modal
        header={renderSurfaceHeader("More Staff Access Needed", "Choose how you want to continue adding staff.", "pi pi-users")}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowStaffUnlockDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7 }}>
            You have reached the maximum number of staff members included right now. Archive a staff member or update Billing to add more active staff.
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              text
              rounded
              onClick={() => setShowStaffUnlockDialog(false)}
            />
            <Button
              type="button"
              label="Billing"
              severity="secondary"
              outlined
              rounded
              onClick={() => {
                setShowStaffUnlockDialog(false);
                toggleModal(false);
                navigate(`${RouterAddress.BILLING}?tab=subscription`);
              }}
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showInvitationSentDialog}
        modal
        closable={false}
        header={renderSurfaceHeader("Invitation Sent", "The staff account is ready for sign-in.", "pi pi-check-circle")}
        style={mobile ? { width: "92vw" } : { width: "30rem", maxWidth: "95vw" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => { window.location.reload(); }}
        footer={<div className="flex justify-content-end"><Button type="button" label="Done" severity="warning" rounded onClick={() => { window.location.reload(); }} /></div>}
      >
        <div className="flex flex-column gap-2" style={{ lineHeight: 1.6 }}>
          <div>Invitation email has been sent to staff with temporary password.</div>
          {invitationSentEmail ? <div style={{ color: "#64748b" }}>Sent to: {invitationSentEmail}</div> : null}
        </div>
      </Dialog>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.65rem", flexWrap: "wrap", marginBottom: "1.1rem", padding: "0.35rem 0" }}>
        {stepItems.map((item, index) => {
          const active = currentStep === item.step;
          const complete = currentStep > item.step;
          return (
            <React.Fragment key={item.step}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: active ? "#c2410c" : complete ? "#15803d" : "#64748b", fontWeight: active ? 800 : 650, fontSize: "0.92rem", whiteSpace: "nowrap", padding: "0.35rem 0.45rem", borderRadius: "8px", background: active ? "#fff7ed" : "transparent", borderBottom: active ? "2px solid #f97316" : "2px solid transparent" }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 800 }}>{index + 1}.</span>
                <span>{item.label}</span>
              </div>
              {index < stepItems.length - 1 ? <i className="pi pi-chevron-right" style={{ color: "#cbd5e1", fontSize: "0.82rem" }} aria-hidden="true" /> : null}
            </React.Fragment>
          );
        })}
      </div>

      {currentStep === 1 ? (
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem", textAlign: "center" }}>Staff Email</div>
            <InputText
              value={searchEmailsInput}
              onChange={(event) => setSearchEmailsInput(event.target.value)}
              type="email"
              placeholder="Enter staff email address"
              style={{ width: "min(24rem, 100%)" }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void onSearchSubmit();
                }
              }}
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-content-center">
            <Button
              type="button"
              label="Search Staff"
              severity="warning"
              rounded
              outlined
              loading={loading}
              onClick={() => void onSearchSubmit()}
            />
          </div>

          {unmatchedEmails.length ? (
            <div style={{ ...appAccentPanelStyle, background: "#fff7ed", borderColor: "#fed7aa" }}>
              <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                <div style={{ color: "#9a3412", fontWeight: 700 }}>Account doesn&apos;t exist in Hub. Configure access now, after all setup, the system will send an invitation email to the user.</div>
                {unmatchedEmails.map((email) => {
                  const normalizedEmail = String(email || "").trim().toLowerCase();
                  return <Button key={normalizedEmail} type="button" label="Configure Access" severity="warning" outlined rounded onClick={() => onConfigureInvitedStaff(normalizedEmail)} />;
                })}
              </div>
            </div>
          ) : null}

          {matchedUsers.length ? (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", background: "#ffffff" }}>
              <div
                style={{
                  padding: "0.85rem 1rem",
                  borderBottom: "1px solid #e2e8f0",
                  background: "#fffdf5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ color: "#475569", lineHeight: 1.5 }}>Select Configure for one staff member to continue.</div>
              </div>
              {!mobile ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: "0.75rem",
                    padding: "0.8rem 1rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <div>Staff</div>
                  <div>Existing Hub Role</div>
                  <div>Action</div>
                </div>
              ) : null}

              {matchedUsers.map((user) => {
                const userId = String(user?.userId || "").trim();
                const roleTypes = Array.isArray(user?.roleTypes) ? user.roleTypes : [];
                const linkedStaff = linkedStaffByUserId[userId];
                const alreadyLinked = linkedStaffIds.has(userId);
                const archivedLinked = alreadyLinked && isArchivedLinkedStaff(linkedStaff);
                return (
                  <div
                    key={userId || user.email}
                    style={{
                      display: mobile ? "flex" : "grid",
                      flexDirection: mobile ? "column" : undefined,
                      gridTemplateColumns: mobile ? undefined : "repeat(3, minmax(0, 1fr))",
                      gap: mobile ? "0.65rem" : "0.75rem",
                      padding: "0.9rem 1rem",
                      borderBottom: "1px solid #eef2f7",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {`${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "Staff"}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem", marginTop: "0.2rem" }}>
                        {user?.email || "Not available"}
                      </div>
                    </div>
                    <div className="flex flex-column gap-1" style={{ minWidth: 0 }}>
                      {mobile ? <div style={{ width: "100%", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Existing Hub Role</div> : null}
                      {roleTypes[0] ? <div className="flex align-items-center gap-2">
                        <span style={{ minWidth: "4.6rem", color: "#64748b", fontSize: "0.8rem" }}>Primary</span>
                        <span style={{ color: "#0f172a", fontWeight: 600 }}>{formatRoleLabel(roleTypes[0])}</span>
                      </div> : null}
                      {roleTypes.length > 1 ? <div className="flex align-items-center gap-2">
                        <span style={{ minWidth: "4.6rem", color: "#64748b", fontSize: "0.8rem" }}>Secondary</span>
                        <span style={{ color: "#0f172a", fontWeight: 600 }}>{roleTypes.slice(1).map(formatRoleLabel).join(", ")}</span>
                      </div> : null}
                    </div>
                    <div className="flex flex-column gap-2 align-items-start">
                      {mobile ? <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Action</div> : null}
                      {alreadyLinked ? (
                        <div className="flex flex-wrap gap-2">
                          <Tag value="Already linked" severity="success" />
                          {archivedLinked ? (
                            <Tag value="Archived" severity="warning" />
                          ) : null}
                        </div>
                      ) : (
                        <Button
                          type="button"
                          label="Configure"
                          severity="warning"
                          rounded
                          loading={loading || loadingLinkedStaff}
                          onClick={() => void onAddStaffClick(user)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {currentStep === 2 && selectedStaffRequiresInvite ? (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {wizardAccessStepError ? <div style={{ border: "1px solid #fdba74", background: "#fff7ed", color: "#9a3412", borderRadius: "14px", padding: "0.85rem 0.95rem", fontWeight: 600, lineHeight: 1.6 }}>{wizardAccessStepError}</div> : null}
          <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Profile</div>
            <div className="grid">
              <div className="col-12 md:col-6"><InputText value={staffNameInput} onChange={(event) => setStaffNameInput(event.target.value)} placeholder="Full name" className="w-full" required /></div>
              <div className="col-12 md:col-6"><InputText value={selectedStaffUsers.map((user) => user.email || "").filter(Boolean).join(", ")} disabled placeholder="Email" className="w-full" /></div>
              <div className="col-12 md:col-6"><InputText type="tel" value={staffPhoneInput} onChange={(event) => setStaffPhoneInput(event.target.value)} placeholder="Phone number" className="w-full" required /></div>
            </div>
          </div>
        </div>
      ) : null}

      {currentStep === 3 && !isFacilityWorkerSelected ? (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {wizardAccessStepError ? (
            <div
              style={{
                border: "1px solid #fdba74",
                background: "#fff7ed",
                color: "#9a3412",
                borderRadius: "14px",
                padding: "0.85rem 0.95rem",
                fontWeight: 600,
                lineHeight: 1.6,
              }}
            >
              {wizardAccessStepError}
            </div>
          ) : null}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1.1fr)",
              gap: "0.9rem",
              alignItems: "start",
            }}
          >
            <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Select Role</div>
                <Tag
                  value="View Permissions"
                  severity="warning"
                  rounded
                  onClick={() => setShowAccessMatrixDialog(true)}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: "0.85rem" }}>
                Click a role card if you want to apply a role-based access setup now.
              </div>
              {wizardAccessPresetOptions.length ? (
                <div className="flex flex-column gap-3">
                  {wizardAccessPresetOptions.map((option) => (
                    <label
                      key={option.value}
                      htmlFor={`wizard-access-preset-${option.value}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.7rem",
                        border: selectedWizardAccessPreset === option.value ? "1px solid #fdba74" : "1px solid #dbe7f3",
                        background: selectedWizardAccessPreset === option.value ? "#fff7ed" : "#ffffff",
                        borderRadius: "14px",
                        padding: "0.8rem 0.9rem",
                        cursor: "pointer",
                      }}
                    >
                      <RadioButton
                        inputId={`wizard-access-preset-${option.value}`}
                        name="wizardAccessPreset"
                        value={option.value}
                        checked={selectedWizardAccessPreset === option.value}
                        onChange={(event) => {
                          applyWizardAccessPreset(String(event.value || ""));
                          setWizardAccessStepError("");
                        }}
                        style={{ marginTop: "0.1rem" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{option.label}</span>
                        <span style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.5 }}>{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                  No role templates are available for this staff selection.
                </div>
              )}
            </div>

            <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Select Residents</div>
                <div className="flex gap-2 flex-wrap align-items-center">
                  {activeResidents.length ? (
                    <>
                      <Button
                        type="button"
                        label="Select All"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={() => setSelectedResidentIds(activeResidents.map((resident) => String(resident?.residentId || "").trim()).filter(Boolean))}
                      />
                      <Button
                        type="button"
                        label="Clear"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={() => setSelectedResidentIds([])}
                      />
                    </>
                  ) : null}
                </div>
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: "0.85rem" }}>
                Click any resident row to give this staff member shared resident access now.
              </div>
              {activeResidents.length ? (
                <div className="flex flex-column gap-3">
                  {activeResidents.map((resident) => {
                    const residentId = String(resident?.residentId || "").trim();
                    const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident";
                    const checked = selectedResidentIds.includes(residentId);

                    return (
                      <div
                        key={residentId}
                        style={{
                          border: checked ? "1px solid #fdba74" : "1px solid #dbe7f3",
                          background: checked ? "#fff7ed" : "#ffffff",
                          borderRadius: "14px",
                          padding: "0.85rem 0.95rem",
                        }}
                      >
                        <label
                          htmlFor={`wizard-caregiver-share-${residentId}`}
                          style={{ display: "flex", alignItems: "center", gap: "0.85rem", cursor: "pointer" }}
                        >
                          <Checkbox
                            inputId={`wizard-caregiver-share-${residentId}`}
                            checked={checked}
                            onChange={(e) => {
                              setWizardAccessStepError("");
                              setSelectedResidentIds((prev) =>
                                e.checked
                                  ? [...prev, residentId]
                                  : prev.filter((item) => item !== residentId),
                              );
                            }}
                          />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{residentName}</div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                  No active residents available yet. You can continue and set resident access later.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        visible={showAccessMatrixDialog}
        header="Access Matrix"
        style={mobile ? { width: "95vw" } : { width: "42rem", maxWidth: "95vw" }}
        modal
        onHide={() => setShowAccessMatrixDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#64748b", lineHeight: 1.6 }}>
            This shows which access is included when you choose each role template.
          </div>
          <DataTable value={STAFF_ACCESS_MATRIX_ROWS} size="small" responsiveLayout="scroll">
            <Column field="access" header="Access" body={(rowData: any) => <StaffAccessMatrixAction row={rowData} />} />
            <Column
              header="Caregiver"
              body={(rowData: any) => <Tag value={rowData.caregiver ? "Included" : "Not included"} severity={rowData.caregiver ? "success" : "secondary"} rounded />}
            />
            <Column
              header="Nurse"
              body={(rowData: any) => <Tag value={rowData.nurse ? "Included" : "Not included"} severity={rowData.nurse ? "success" : "secondary"} rounded />}
            />
            <Column
              header="MHP"
              body={(rowData: any) => <Tag value={rowData.mhp ? "Included" : "Not included"} severity={rowData.mhp ? "success" : "secondary"} rounded />}
            />
          </DataTable>
        </div>
      </Dialog>

      {currentStep === 4 ? (
        <div style={{ display: "grid", gap: "1rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "0.9rem",
              alignItems: "start",
            }}
          >
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "16px",
                background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                padding: "0.85rem 0.95rem",
                width: "100%",
              }}
            >
              <div style={{ color: "#64748b", fontSize: "0.85rem", lineHeight: 1.5, marginBottom: "0.65rem" }}>
                When this is on, staff can use use their mobile phones. Turn it off if they should only access shared resident data from desktop or laptop.
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", cursor: "pointer" }}>
                <Checkbox
                  inputId="wizard-caregiver-mobile-access"
                  checked={mobileAccessEnabled}
                  onChange={(event) => setMobileAccessEnabled(Boolean(event.checked))}
                  style={{ marginTop: "0.1rem" }}
                />
                <div style={{ display: "grid", gap: "0.35rem" }}>
                  <span style={{ fontWeight: 700, color: "#0f172a" }}>Allow mobile access</span>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", cursor: "pointer", marginTop: "0.7rem" }}>
                <Checkbox
                  inputId="wizard-caregiver-all-time-access"
                  checked={allTimeShiftAccessEnabled}
                  onChange={(event) => setAllTimeShiftAccessEnabled(Boolean(event.checked))}
                  style={{ marginTop: "0.1rem" }}
                />
                <div style={{ display: "grid", gap: "0.2rem" }}>
                  <span style={{ fontWeight: 700, color: "#0f172a" }}>All time access (24/7)</span>
                  <span style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.45 }}>
                    Save full-time scheduled access for every day of the week.
                  </span>
                </div>
              </label>
            </div>
            <div style={{ display: "grid", gap: "0.85rem" }}>
              <div style={{ color: "#64748b", lineHeight: 1.6 }}>
                Set each day’s access window for this caregiver. If no day is enabled, scheduled access stays off and they can access any time.
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" label="Weekday Day Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "07:00", "19:00"))} />
                <Button type="button" label="Weekday Night Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "19:00", "07:00"))} />
                <Button type="button" label="Every Day Day Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "07:00", "19:00"))} />
                <Button type="button" label="Every Day Night Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "19:00", "07:00"))} />
                <Button type="button" label="Clear All" severity="contrast" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => clearShiftSchedule(current))} />
              </div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(0, 20rem) minmax(0, 33.5rem)",
              gap: "1rem",
              justifyContent: "center",
              alignItems: "start",
            }}
          >
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "16px",
                padding: "0.8rem 0.85rem",
                background: "#f8fafc",
                width: "100%",
              }}
            >
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem", fontSize: "0.95rem" }}>Apply Same Time to Every Day</div>
              <div style={{ display: "grid", gap: "0.65rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "0.55rem" }}>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.69rem", fontWeight: 700, marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Start Time</div>
                    <select
                      value={bulkEveryDayStartTime}
                      disabled={allTimeShiftAccessEnabled}
                      onChange={(event) => setBulkEveryDayStartTime(event.target.value)}
                      style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem", opacity: allTimeShiftAccessEnabled ? 0.6 : 1 }}
                    >
                      {HALF_HOUR_TIME_OPTIONS.map((option) => (
                        <option key={`bulk-start-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.69rem", fontWeight: 700, marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>End Time</div>
                    <select
                      value={bulkEveryDayEndTime}
                      disabled={allTimeShiftAccessEnabled}
                      onChange={(event) => setBulkEveryDayEndTime(event.target.value)}
                      style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem", opacity: allTimeShiftAccessEnabled ? 0.6 : 1 }}
                    >
                      {HALF_HOUR_TIME_OPTIONS.map((option) => (
                        <option key={`bulk-end-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Button
                    type="button"
                    label="Apply to Every Day"
                    severity="warning"
                    rounded
                    outlined
                    disabled={allTimeShiftAccessEnabled}
                    className={mobile ? "w-full" : undefined}
                    style={mobile ? undefined : { minHeight: 34, padding: "0.45rem 0.85rem" }}
                    onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), bulkEveryDayStartTime, bulkEveryDayEndTime))}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", background: "#ffffff", width: "100%", maxWidth: mobile ? "100%" : "33.5rem" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "1fr" : "minmax(74px, 0.7fr) minmax(68px, 0.42fr) minmax(112px, 0.85fr) minmax(112px, 0.85fr)",
                    gap: "0.45rem",
                    padding: "0.58rem 0.72rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: "0.69rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <div>Day</div>
                  {!mobile ? <div>On</div> : null}
                  {!mobile ? <div>Start</div> : null}
                  {!mobile ? <div>End</div> : null}
                </div>
                {dailyShiftSchedule.map((daySchedule) => (
                  <div
                    key={daySchedule.day}
                    style={{
                      display: "grid",
                      gridTemplateColumns: mobile ? "1fr" : "minmax(74px, 0.7fr) minmax(68px, 0.42fr) minmax(112px, 0.85fr) minmax(112px, 0.85fr)",
                      gap: "0.45rem",
                      padding: "0.5rem 0.72rem",
                      borderBottom: "1px solid #eef2f7",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.86rem" }}>{daySchedule.label}</div>
                    <div style={{ display: "flex", justifyContent: mobile ? "flex-start" : "center" }}>
                      <Checkbox
                        checked={daySchedule.enabled}
                        disabled={allTimeShiftAccessEnabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, enabled: Boolean(event.checked) } : item));
                        }}
                      />
                    </div>
                    <div>
                      <select
                        value={daySchedule.startTime}
                        disabled={!daySchedule.enabled || allTimeShiftAccessEnabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, startTime: event.target.value } : item));
                        }}
                        style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem" }}
                      >
                        {HALF_HOUR_TIME_OPTIONS.map((option) => (
                          <option key={`${daySchedule.day}-start-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={daySchedule.endTime}
                        disabled={!daySchedule.enabled || allTimeShiftAccessEnabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, endTime: event.target.value } : item));
                        }}
                        style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem" }}
                      >
                        {HALF_HOUR_TIME_OPTIONS.map((option) => (
                          <option key={`${daySchedule.day}-end-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ color: "#64748b", fontSize: "0.88rem" }}>
            {allTimeShiftAccessEnabled
              ? "24/7 access will be saved as full-day windows for all seven days."
              : hasAnyEnabledShiftDay
                ? "Enabled days will be saved as per-day access windows."
                : "No day is enabled yet. Saving now will keep scheduled access off."}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
};

export default AddCaregiverModal;
